/**
 * sz-v2-ev — certificate verification UI mount
 * Depends: sz-v2-ev-engineer-api.js (SZ_V2_EV_ENGINEER_API), jsQR (window.jsQR)
 */
(function () {
  'use strict';

  var api = typeof window !== 'undefined' ? window.SZ_V2_EV_ENGINEER_API : null;

  /** 每次读取，避免 cert-verify 早于 jsQR 执行时把 null 缓存死 */
  function getJsQR() {
    return typeof window !== 'undefined' && typeof window.jsQR === 'function' ? window.jsQR : null;
  }

  function fmtDate(ms) {
    if (ms == null) return '';
    var d = new Date(ms);
    if (isNaN(d.getTime())) return '';
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function holderName(data, emptyCell) {
    var dash = emptyCell != null ? String(emptyCell) : '—';
    var a = (data.first_name || '').trim();
    var b = (data.last_name || '').trim();
    return (a + ' ' + b).trim() || dash;
  }

  function imgSrc(img) {
    if (!img || typeof img !== 'string') return '';
    var s = img.trim();
    if (!s) return '';
    if (s.indexOf('data:') === 0 || /^https?:\/\//i.test(s)) return s;
    return s;
  }

  function setHidden(el, hidden) {
    if (!el) return;
    el.hidden = !!hidden;
    el.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  function extractSnFromQrPayload(raw) {
    if (raw == null) return '';
    var t = String(raw).trim();
    if (!t) return '';
    if (t.charAt(0) === '{') {
      try {
        var o = JSON.parse(t);
        if (o && typeof o === 'object') {
          if (o.sn != null) return String(o.sn).trim();
          if (o.SN != null) return String(o.SN).trim();
          if (o.serial != null) return String(o.serial).trim();
        }
      } catch (e) {}
    }
    try {
      var u = new URL(t);
      var snq = u.searchParams.get('sn') || u.searchParams.get('SN');
      if (snq) return snq.trim();
      var parts = u.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
      if (parts.length) {
        var last = parts[parts.length - 1];
        if (last && !/\.(png|jpe?g|gif|webp|svg)$/i.test(last)) return last.trim();
      }
    } catch (e2) {}
    return t;
  }

  /**
   * @param {string} path
   * @returns {string}
   */
  function normalizeUrlPath(path) {
    var p = String(path || '').trim();
    if (!p || p === '/') return '/';
    return p.replace(/\/+$/, '') || '/';
  }

  /**
   * 当前页面 URL 查询参数中的证书编号（sn / SN）。
   * @returns {string}
   */
  function getSnFromUrlParams() {
    if (typeof window === 'undefined' || !window.location) return '';
    try {
      var params = new URLSearchParams(window.location.search);
      var sn = params.get('sn') || params.get('SN');
      return sn ? String(sn).trim() : '';
    } catch (e) {
      return '';
    }
  }

  /**
   * 仅当二维码为指定店铺证书页 URL 且含 sn 时返回编号。
   * @param {string} raw
   * @param {string} allowedBase 例：https://shop.myshopify.com/pages/ev
   * @returns {string}
   */
  function extractSnFromEvLink(raw, allowedBase) {
    var baseStr = String(allowedBase || '').trim();
    if (!baseStr) return '';
    var t = String(raw || '').trim();
    if (!t) return '';
    try {
      var baseInput = baseStr.indexOf('://') === -1 ? 'https://' + baseStr : baseStr;
      var u = new URL(t);
      var bu = new URL(baseInput);
      if (u.origin !== bu.origin) return '';
      if (normalizeUrlPath(u.pathname) !== normalizeUrlPath(bu.pathname)) return '';
      var snq = u.searchParams.get('sn') || u.searchParams.get('SN');
      return snq ? String(snq).trim() : '';
    } catch (e) {
      return '';
    }
  }

  /**
   * URL 含 sn 时滚动到证书核验区块锚点，并保留 query 同步 hash。
   * @param {HTMLElement} el
   */
  function scrollToCertVerifyAnchor(el) {
    if (!el || typeof window === 'undefined') return;
    var id = el.id;
    if (id) {
      var hash = '#' + id;
      if (window.location.hash !== hash) {
        var next = window.location.pathname + window.location.search + hash;
        try {
          history.replaceState(null, '', next);
        } catch (e) {
          window.location.hash = id;
        }
      }
    }
    window.requestAnimationFrame(function () {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  }

  function mount(root) {
    if (!root || root.getAttribute('data-mounted') === '1') return;
    if (!api) console.warn('sz-v2-ev-cert-verify: SZ_V2_EV_ENGINEER_API missing — query will fail until script loads');

    var cfgEl = root.querySelector('script.sz-v2-ev-cert-verify__cfg[type="application/json"]');
    /** @type {Record<string, string>} */
    var cfg = {};
    try {
      cfg = cfgEl ? JSON.parse(cfgEl.textContent || '{}') : {};
    } catch (e) {
      cfg = {};
    }

    var emptyCell = cfg.emptyValue != null ? String(cfg.emptyValue) : '—';

    /** @param {string} step @param {unknown} [detail] */
    function qrTrace(step, detail) {
      if (typeof console === 'undefined' || !console.log) return;
      if (detail !== undefined) console.log('[sz-v2-ev-cert-verify][qr]', step, detail);
      else console.log('[sz-v2-ev-cert-verify][qr]', step);
    }

    var apiBase = cfg.apiBase || '';
    var searchPanel = root.querySelector('[data-panel="search"]');
    var resultPanel = root.querySelector('[data-panel="result"]');
    var input = /** @type {HTMLInputElement|null} */ (root.querySelector('[data-field="sn"]'));
    var btnQuery = root.querySelector('[data-action="query"]');
    var btnAgain = root.querySelector('[data-action="again"]');
    var loadingEl = root.querySelector('[data-state="loading"]');

    var statusIcon = root.querySelector('[data-result="status-icon"]');
    var statusTitle = root.querySelector('[data-result="status-title"]');
    var statusSub = root.querySelector('[data-result="status-sub"]');

    var rowLevel = root.querySelector('[data-row="level"]');
    var rowSn = root.querySelector('[data-row="sn"]');
    var rowHolder = root.querySelector('[data-row="holder"]');
    var rowIssue = root.querySelector('[data-row="issue"]');
    var rowExpire = root.querySelector('[data-row="expire"]');
    var rowModels = root.querySelector('[data-row="models"]');

    var certImg = /** @type {HTMLImageElement|null} */ (root.querySelector('[data-result="cert-img"]'));
    var certWrap = root.querySelector('[data-result="cert-wrap"]');
    var certPlaceholder = root.querySelector('[data-result="cert-placeholder"]');

    var fileInput = /** @type {HTMLInputElement|null} */ (root.querySelector('[data-field="qr-file"]'));
    var uploadZone = root.querySelector('[data-upload-zone]');
    var qrMsg = root.querySelector('[data-qr-msg]');
    var qrLive = root.querySelector('[data-qr-live]');
    var previewImg = /** @type {HTMLImageElement|null} */ (root.querySelector('[data-upload-preview]'));
    /** @type {string|null} */
    var uploadPreviewObjectUrl = null;
    /** @type {number|null} */
    var qrLiveClearTimer = null;

    function announceQrLive(text) {
      if (!qrLive || !text) return;
      if (qrLiveClearTimer != null) {
        window.clearTimeout(qrLiveClearTimer);
        qrLiveClearTimer = null;
      }
      qrLive.textContent = '';
      window.requestAnimationFrame(function () {
        qrLive.textContent = text;
        qrLiveClearTimer = window.setTimeout(function () {
          qrLive.textContent = '';
          qrLiveClearTimer = null;
        }, 2600);
      });
    }

    function setUploadScanning(on) {
      if (!uploadZone) return;
      uploadZone.classList.toggle('is-scanning', !!on);
    }

    function clearUploadPreview() {
      if (uploadPreviewObjectUrl) {
        URL.revokeObjectURL(uploadPreviewObjectUrl);
        uploadPreviewObjectUrl = null;
      }
      if (previewImg) {
        previewImg.removeAttribute('src');
        previewImg.hidden = true;
      }
      if (uploadZone) uploadZone.classList.remove('has-preview');
    }

    function setUploadPreview(file) {
      if (!previewImg || !file) return;
      clearUploadPreview();
      uploadPreviewObjectUrl = URL.createObjectURL(file);
      previewImg.src = uploadPreviewObjectUrl;
      previewImg.hidden = false;
      if (uploadZone) uploadZone.classList.add('has-preview');
    }

    function setQrMsg(text, isError) {
      if (!qrMsg) return;
      if (!text) {
        qrMsg.hidden = true;
        qrMsg.textContent = '';
        qrMsg.classList.remove('is-error');
        return;
      }
      qrMsg.hidden = false;
      qrMsg.textContent = text;
      qrMsg.classList.toggle('is-error', !!isError);
    }

    function decodeQrFromFile(file, done) {
      qrTrace('step decode: start', { name: file.name, size: file.size, type: file.type });
      var jsQR = getJsQR();
      if (!jsQR) {
        qrTrace('step decode: FAIL jsQR missing', {
          typeofWindowJsQR: typeof window !== 'undefined' ? typeof window.jsQR : 'no-window',
        });
        done(new Error('no jsQR'), null);
        return;
      }
      var blobUrl = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(blobUrl);
        try {
          var w = img.naturalWidth;
          var h = img.naturalHeight;
          qrTrace('step decode: image loaded', { naturalWidth: w, naturalHeight: h });
          if (!w || !h) {
            qrTrace('step decode: FAIL zero dimensions', { w: w, h: h });
            done(new Error('size'), null);
            return;
          }
          var max = 1600;
          var scale = Math.min(1, max / Math.max(w, h));
          var cw = Math.max(1, Math.round(w * scale));
          var ch = Math.max(1, Math.round(h * scale));
          qrTrace('step decode: canvas', { scale: scale, canvasWidth: cw, canvasHeight: ch });
          var canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          var ctx = canvas.getContext('2d');
          if (!ctx) {
            qrTrace('step decode: FAIL no 2d context', {});
            done(new Error('ctx'), null);
            return;
          }
          ctx.drawImage(img, 0, 0, cw, ch);
          var imageData = ctx.getImageData(0, 0, cw, ch);
          var code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'attemptBoth',
          });
          if (!code || !code.data) {
            qrTrace('step decode: FAIL jsQR returned no payload (try clearer crop / higher contrast)', {
              hasCodeObject: !!code,
            });
            done(null, null);
            return;
          }
          qrTrace('step decode: OK raw payload string', code.data);
          done(null, code.data);
        } catch (err) {
          qrTrace('step decode: FAIL exception', err);
          done(err, null);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(blobUrl);
        qrTrace('step decode: FAIL <img> load error (file not a decodable image)', {});
        done(new Error('img'), null);
      };
      img.src = blobUrl;
    }

    /**
     * 拖拽/部分系统下 file.type 可能为空，仅靠 MIME 会误判为非图片
     * @param {File|null|undefined} file
     */
    function isImageFile(file) {
      if (!file) return false;
      if (/^image\//i.test(file.type)) return true;
      var name = (file.name && String(file.name)) || '';
      return /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/i.test(name);
    }

    function scanImageFile(file) {
      setUploadScanning(false);
      qrTrace('step scan: start', { name: file.name, size: file.size, type: file.type });
      if (!isImageFile(file)) {
        qrTrace('step scan: FAIL not accepted as image (MIME/extension)', {
          name: file.name,
          type: file.type,
        });
        clearUploadPreview();
        setQrMsg(cfg.qrBadImage || 'Could not read image.', true);
        return;
      }
      setQrMsg('', false);
      setUploadPreview(file);
      if (!getJsQR()) {
        qrTrace('step scan: FAIL jsQR not on window before decode', {
          typeofJsQR: typeof window !== 'undefined' ? typeof window.jsQR : 'no-window',
        });
        setQrMsg(cfg.qrNoJsQR || 'QR library not loaded.', true);
        return;
      }
      qrTrace('step scan: jsQR OK, scheduling decode', {
        qrStrictEvLink: cfg.qrStrictEvLink !== false,
        qrEvLinkBase: cfg.qrEvLinkBase || '(empty)',
      });
      var scanningSay = cfg.qrScanning != null ? String(cfg.qrScanning).trim() : '';
      if (scanningSay) announceQrLive(scanningSay);
      window.setTimeout(function () {
        setUploadScanning(true);
        decodeQrFromFile(file, function (err, data) {
          setUploadScanning(false);
          if (err) {
            qrTrace('step scan: decode callback error', {
              message: err.message || String(err),
              hint:
                err.message === 'size'
                  ? 'image dimensions'
                  : err.message === 'ctx'
                    ? 'canvas'
                    : err.message === 'img'
                      ? 'image load'
                      : err.message === 'no jsQR'
                        ? 'library'
                        : 'other',
            });
            if (err.message === 'no jsQR') {
              setQrMsg(cfg.qrNoJsQR || 'QR library not loaded.', true);
              return;
            }
            setQrMsg(cfg.qrBadImage || 'Could not read image.', true);
            return;
          }
          if (data == null) {
            qrTrace('step scan: FAIL no QR in image (decode returned null)', {});
            setQrMsg(cfg.qrNoQr || 'No QR code found.', true);
            return;
          }
          var strictEv = cfg.qrStrictEvLink !== false;
          qrTrace('step parse: raw payload', data);
          qrTrace('step parse: mode', { strictEvLink: strictEv, allowedBase: cfg.qrEvLinkBase || '' });
          var sn = '';
          if (strictEv) {
            sn = extractSnFromEvLink(data, cfg.qrEvLinkBase || '');
            if (!sn) {
              try {
                var pu = new URL(String(data).trim());
                qrTrace('step parse: FAIL strict link mismatch or missing sn', {
                  payloadOrigin: pu.origin,
                  payloadPath: pu.pathname,
                  snQuery: pu.searchParams.get('sn') || pu.searchParams.get('SN'),
                  allowedBase: cfg.qrEvLinkBase || '',
                });
              } catch (parseErr) {
                qrTrace('step parse: FAIL strict mode — payload is not a valid URL', {
                  payloadPreview: String(data).slice(0, 160),
                });
              }
              setQrMsg(cfg.qrWrongLink || cfg.qrNoQr || 'Invalid certificate QR link.', true);
              return;
            }
          } else {
            sn = extractSnFromQrPayload(data);
            if (!sn) {
              qrTrace('step parse: FAIL loose mode — empty sn after extractSnFromQrPayload', {});
              var looseMsg = (cfg.qrNoSn != null && String(cfg.qrNoSn).trim() !== '') ? String(cfg.qrNoSn).trim() : cfg.qrNoQr;
              setQrMsg(looseMsg || 'No certificate number in QR.', true);
              return;
            }
          }
          qrTrace('step scan: OK certificate number (sn)', sn);
          if (input) {
            input.value = sn;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.focus();
          }
          setQrMsg('', false);
          var okSay = cfg.qrOk != null ? String(cfg.qrOk).trim() : '';
          announceQrLive(okSay || 'Certificate number filled from your photo.');
        });
      }, 0);
    }

    function showLoading(on) {
      setHidden(loadingEl, !on);
      root.classList.toggle('is-loading', !!on);
      if (btnQuery && 'disabled' in btnQuery) /** @type {HTMLButtonElement} */ (btnQuery).disabled = !!on;
    }

    function showSearchView() {
      setHidden(searchPanel, false);
      setHidden(resultPanel, true);
      root.classList.remove('sz-v2-ev-cert--result');
    }

    function showResultView() {
      setHidden(searchPanel, true);
      setHidden(resultPanel, false);
      root.classList.add('sz-v2-ev-cert--result');
    }

    function applyStatus(kind, title, sub, issueHighlight) {
      root.setAttribute('data-verify-state', kind);
      if (statusTitle) statusTitle.textContent = title || '';
      if (statusSub) statusSub.textContent = sub || '';
      if (statusIcon) {
        statusIcon.textContent = kind === 'success' ? '\u2713' : '!';
        statusIcon.classList.remove('is-success', 'is-error', 'is-warn');
        if (kind === 'success') statusIcon.classList.add('is-success');
        else if (kind === 'error' || kind === 'none') statusIcon.classList.add('is-error');
        else if (kind === 'expired') statusIcon.classList.add('is-warn');
      }
      if (rowIssue) rowIssue.classList.toggle('is-highlight-bad', !!issueHighlight);
    }

    function fillRows(data, querySn) {
      var trainedMs = api.tsToMs(data.trained_at);
      var expiredMs = api.tsToMs(data.expired_at);
      var issueStr = fmtDate(trainedMs);
      var expStr = '';
      if (trainedMs != null && expiredMs != null)
        expStr = fmtDate(trainedMs) + ' ~ ' + fmtDate(expiredMs);
      else if (expiredMs != null) expStr = fmtDate(expiredMs);

      if (rowLevel) rowLevel.textContent = data.level || emptyCell;
      if (rowSn) rowSn.textContent = data.sn || querySn || emptyCell;
      if (rowHolder) rowHolder.textContent = holderName(data, emptyCell);
      if (rowIssue) rowIssue.textContent = issueStr || emptyCell;
      if (rowExpire) rowExpire.textContent = expStr || emptyCell;
      if (rowModels)
        rowModels.textContent = api.formatTrainingModels(data.training_models) || emptyCell;

      var src = imgSrc(data.img || '');
      var verifyState = root.getAttribute('data-verify-state') || '';
      var showCertPlaceholder = !src && verifyState === 'none';

      if (certImg && certWrap) {
        if (src) {
          certImg.src = src;
          certImg.alt = cfg.altCertificate || 'Certificate';
          setHidden(certImg, false);
          if (certPlaceholder) setHidden(certPlaceholder, true);
          setHidden(certWrap, false);
        } else if (showCertPlaceholder && certPlaceholder) {
          certImg.removeAttribute('src');
          certImg.alt = '';
          setHidden(certImg, true);
          setHidden(certPlaceholder, false);
          setHidden(certWrap, false);
        } else {
          certImg.removeAttribute('src');
          certImg.alt = '';
          setHidden(certImg, true);
          if (certPlaceholder) setHidden(certPlaceholder, true);
          setHidden(certWrap, true);
        }
      }
    }

    function handleEnvelope(envelope, querySn) {
      var code = envelope && typeof envelope.code === 'number' ? envelope.code : -1;
      var msg = (envelope && envelope.message) || '';
      var data = envelope && envelope.data;

      var now = Date.now();

      if (code !== 0 || !data) {
        applyStatus('none', cfg.titleNotFound || 'No matching certificate found.', msg || '', false);
        fillRows(
          {
            level: '',
            sn: querySn,
            first_name: '',
            last_name: '',
            trained_at: undefined,
            expired_at: undefined,
            training_models: [],
            img: '',
          },
          querySn
        );
        showResultView();
        return;
      }

      var expiredMs = api.tsToMs(data.expired_at);
      var isExpired = expiredMs != null && expiredMs < now;

      if (isExpired) {
        applyStatus(
          'expired',
          cfg.titleFailed || 'Verification Failed',
          cfg.subExpired || 'Certificate has expired.',
          true
        );
      } else {
        applyStatus(
          'success',
          cfg.titleOk || 'Verification Successful',
          cfg.subOk || 'Certificate is Valid',
          false
        );
      }

      fillRows(data, querySn);
      showResultView();
    }

    function runQuery() {
      if (!input || !api) return;
      var sn = input.value.trim();
      if (!sn) {
        input.focus();
        return;
      }
      showLoading(true);
      api
        .fetchCertificate(apiBase, sn)
        .then(function (env) {
          handleEnvelope(env, sn);
        })
        .catch(function () {
          applyStatus('none', cfg.titleNotFound || 'No matching certificate found.', cfg.subNetwork || 'Request failed.', false);
          fillRows(
            {
              level: '',
              sn: sn,
              first_name: '',
              last_name: '',
              trained_at: undefined,
              expired_at: undefined,
              training_models: [],
              img: '',
            },
            sn
          );
          showResultView();
        })
        .finally(function () {
          showLoading(false);
        });
    }

    if (btnQuery) btnQuery.addEventListener('click', runQuery);
    if (input)
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') runQuery();
      });
    if (btnAgain)
      btnAgain.addEventListener('click', function () {
        showSearchView();
        setUploadScanning(false);
        setQrMsg('', false);
        if (qrLiveClearTimer != null) {
          window.clearTimeout(qrLiveClearTimer);
          qrLiveClearTimer = null;
        }
        if (qrLive) qrLive.textContent = '';
        clearUploadPreview();
        if (input) input.focus();
      });

    if (fileInput && uploadZone) {
      fileInput.addEventListener('change', function () {
        var f = fileInput.files && fileInput.files[0];
        if (f) scanImageFile(f);
        fileInput.value = '';
      });

      /** 捕获阶段：拖放命中子节点（图标/文案）时也必须 preventDefault，否则浏览器禁止 drop */
      uploadZone.addEventListener(
        'dragenter',
        function (e) {
          e.preventDefault();
          uploadZone.classList.add('is-dragover');
        },
        true
      );
      uploadZone.addEventListener(
        'dragover',
        function (e) {
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        },
        true
      );
      uploadZone.addEventListener(
        'dragleave',
        function (e) {
          e.preventDefault();
          var nextTarget = e.relatedTarget;
          if (!nextTarget || !uploadZone.contains(nextTarget)) {
            uploadZone.classList.remove('is-dragover');
          }
        },
        true
      );
      uploadZone.addEventListener(
        'drop',
        function (e) {
          e.preventDefault();
          uploadZone.classList.remove('is-dragover');
          var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (f) scanImageFile(f);
        },
        true
      );
    }

    var urlSn = getSnFromUrlParams();
    if (urlSn && input) {
      input.value = urlSn;
      scrollToCertVerifyAnchor(root);
      runQuery();
    }

    root.setAttribute('data-mounted', '1');
  }

  function init() {
    document.querySelectorAll('[data-sz-v2-ev-cert-verify]').forEach(mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
